import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FaCheck, FaCode, FaCopy, FaDownload, FaImage, FaSyncAlt, FaTimes, FaUpload } from "react-icons/fa";

const maxImageBytes = 8 * 1024 * 1024;

const supportedImage = (file) => {
  if (!file) return false;
  if (file.type) return ["image/png", "image/jpeg", "image/webp"].includes(file.type);
  return /\.(png|jpe?g|webp)$/i.test(file.name || "");
};

const waitForNextPoll = () => new Promise((resolve) => setTimeout(resolve, 1500));

const unwrapJob = (response) => {
  if (!response || response.success === false) {
    throw new Error(response?.error || "圖片修復工作查詢失敗，請重試。");
  }
  return response.job || response;
};

const downloadBaseName = (file) =>
  (file?.name || "image").replace(/\.[^.]+$/, "").replace(/[\\/:*?"<>|]/g, "_");

export default function ImageRedrawWorkspace({
  file,
  onSelectFile,
  startImageRedrawJob,
  getImageRedrawJob,
}) {
  const fileInputRef = useRef(null);
  const codeRef = useRef(null);
  const requestRef = useRef(0);
  const [sourceUrl, setSourceUrl] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [instructions, setInstructions] = useState("");
  const [phase, setPhase] = useState("idle");
  const [message, setMessage] = useState("");
  const [result, setResult] = useState(null);
  const [copyMessage, setCopyMessage] = useState("");
  const busy = phase === "queued" || phase === "running";
  const fileError = file && !supportedImage(file)
    ? "請選擇 PNG、JPG 或 WebP 圖片。"
    : file?.size > maxImageBytes
      ? "圖片上限為 8 MB，請選擇較小的圖片。"
      : "";
  const validFile = Boolean(file) && !fileError;

  useEffect(() => () => { requestRef.current += 1; }, []);

  useEffect(() => {
    requestRef.current += 1;
    setPhase(fileError ? "error" : "idle");
    setMessage(fileError);
    setResult(null);
    setCopyMessage("");
    if (!validFile || typeof URL.createObjectURL !== "function") {
      setSourceUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setSourceUrl(url);
    return () => URL.revokeObjectURL?.(url);
  }, [file, fileError, validFile]);

  const selectFile = (nextFile) => {
    if (!nextFile) return;
    requestRef.current += 1;
    setPhase("idle");
    setMessage("");
    setResult(null);
    setCopyMessage("");
    onSelectFile?.(nextFile);
  };

  const removeFile = () => {
    requestRef.current += 1;
    if (fileInputRef.current) fileInputRef.current.value = "";
    setDragOver(false);
    setSourceUrl("");
    setPhase("idle");
    setMessage("");
    setResult(null);
    setCopyMessage("");
    onSelectFile?.(null);
  };

  const startRedraw = async () => {
    if (busy) return;
    if (!file || !supportedImage(file)) {
      setPhase("error");
      setMessage("請先上傳 PNG、JPG 或 WebP 圖片。");
      return;
    }
    if (!validFile) {
      setPhase("error");
      setMessage("圖片上限為 8 MB，請選擇較小的圖片。");
      return;
    }
    const requestInstructions = instructions.trim();
    if (!requestInstructions) {
      setPhase("error");
      setMessage("請先輸入修復重畫需求。");
      return;
    }
    if (typeof startImageRedrawJob !== "function" || typeof getImageRedrawJob !== "function") {
      setPhase("error");
      setMessage("目前無法使用圖片修復服務，請重新整理。");
      return;
    }

    const token = ++requestRef.current;
    setResult(null);
    setCopyMessage("");
    setPhase("queued");
    setMessage("正在建立圖片修復工作…");
    try {
      const started = unwrapJob(await startImageRedrawJob(file, requestInstructions));
      if (token !== requestRef.current) return;
      if (!started.id) throw new Error("未收到圖片修復工作編號，請重試。");

      let job = started;
      const deadline = Date.now() + 10 * 60 * 1000;
      while (["queued", "running"].includes(job.status)) {
        if (token !== requestRef.current) return;
        setPhase(job.status);
        setMessage(job.status === "queued" ? "圖片修復工作排隊中…" : "正在修復並重畫圖片…");
        if (Date.now() >= deadline) throw new Error("圖片修復等待逾時，請稍後重試。");
        await waitForNextPoll();
        if (token !== requestRef.current) return;
        job = unwrapJob(await getImageRedrawJob(started.id));
      }
      if (token !== requestRef.current) return;
      if (job.status !== "success") {
        throw new Error(job.error || "圖片修復失敗，請調整需求後重試。");
      }
      if (!/^data:image\/png;base64,/i.test(job.image || "") || !String(job.code || "").trim()) {
        throw new Error("修復工作完成，但未收到完整的圖片及 Python 程式碼，請重試。");
      }
      setResult({ image: job.image, code: job.code });
      setPhase("success");
      setMessage("修復重畫完成。請核對圖片與 Python 程式碼。");
    } catch (error) {
      if (token !== requestRef.current) return;
      setPhase("error");
      setMessage(error?.message || "圖片修復失敗，請重試。");
    }
  };

  const copyCode = async () => {
    if (!result?.code) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(result.code);
      } else {
        codeRef.current?.select();
        if (!document.execCommand?.("copy")) throw new Error("copy unavailable");
      }
      setCopyMessage("已複製 Python 程式碼。");
    } catch {
      setCopyMessage("無法自動複製，請選取程式碼後手動複製。");
    }
  };

  return (
    <Root aria-label="圖片修復重畫">
      <Card>
        <Heading><FaImage aria-hidden="true" /><h3>修復重畫圖片</h3></Heading>
        <Description>上傳圖片並描述需要修復的內容；完成後可取得重畫圖片與 Python 程式碼。</Description>

        <UploadArea
          $dragOver={dragOver}
          onDragOver={(event) => { event.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragOver(false);
            selectFile(event.dataTransfer.files?.[0]);
          }}
        >
          <UploadLabel htmlFor="image-redraw-file">
            <FaUpload aria-hidden="true" />
            <strong>{file?.name || "選擇、拖曳或貼上圖片"}</strong>
            <small>PNG、JPG、WebP，最多 8 MB</small>
            <small>可按 ⌘V／Ctrl+V 貼上剪貼簿圖片</small>
          </UploadLabel>
          <FileInput
            ref={fileInputRef}
            id="image-redraw-file"
            type="file"
            accept="image/png,image/jpeg,image/webp"
            aria-label="上傳修復圖片"
            onChange={(event) => {
              selectFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </UploadArea>
        {file && (
          <SelectedImage>
            {sourceUrl && <SourceImage src={sourceUrl} alt="待修復原圖" />}
            <RemoveImageButton type="button" onClick={removeFile}>
              <FaTimes aria-hidden="true" />移除圖片
            </RemoveImageButton>
          </SelectedImage>
        )}

        <FieldLabel htmlFor="image-redraw-instructions">修復重畫需求</FieldLabel>
        <Instructions
          id="image-redraw-instructions"
          value={instructions}
          onChange={(event) => setInstructions(event.target.value)}
          disabled={busy}
          maxLength={2000}
          placeholder="例如：補齊模糊的線條，保留座標、數字與文字，重畫成清晰的數學圖。"
        />
        <SubmitButton type="button" onClick={startRedraw} disabled={busy}>
          {busy ? <FaSyncAlt aria-hidden="true" /> : <FaImage aria-hidden="true" />}
          {busy ? "修復重畫中…" : phase === "error" ? "重試修復重畫" : "開始修復重畫"}
        </SubmitButton>
        {message && (
          <StatusMessage $phase={phase} role={phase === "error" ? "alert" : "status"}>
            {phase === "success" && <FaCheck aria-hidden="true" />}
            {busy && <FaSyncAlt aria-hidden="true" />}
            <span>{message}</span>
          </StatusMessage>
        )}
      </Card>

      {result && (
        <>
          <Card>
            <ResultHeading>
              <Heading><FaImage aria-hidden="true" /><h3>重畫圖片</h3></Heading>
              <ActionLink href={result.image} download={`${downloadBaseName(file)}-redrawn.png`}>
                <FaDownload aria-hidden="true" />下載 PNG
              </ActionLink>
            </ResultHeading>
            <ResultImage src={result.image} alt="修復重畫結果" />
          </Card>
          <Card>
            <ResultHeading>
              <Heading><FaCode aria-hidden="true" /><h3>Python 程式碼</h3></Heading>
              <Actions>
                <ActionButton type="button" onClick={copyCode}><FaCopy aria-hidden="true" />複製</ActionButton>
                <ActionLink
                  href={`data:text/x-python;charset=utf-8,${encodeURIComponent(result.code)}`}
                  download={`${downloadBaseName(file)}-redrawn.py`}
                >
                  <FaDownload aria-hidden="true" />下載 .py
                </ActionLink>
              </Actions>
            </ResultHeading>
            <CodeArea ref={codeRef} aria-label="重畫 Python 程式碼" readOnly value={result.code} />
            {copyMessage && <CopyMessage role="status">{copyMessage}</CopyMessage>}
          </Card>
        </>
      )}
    </Root>
  );
}

const Root = styled.section`
  min-width: 0;
  display: grid;
  align-content: start;
  gap: 14px;
`;

const Card = styled.div`
  min-width: 0;
  display: grid;
  gap: 12px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);

  @media (max-width: 640px) { padding: 12px; }
`;

const Heading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  h3 { margin: 0; font-size: 16px; font-weight: 800; }
  svg { color: var(--app-icon-accent, #202124); }
`;

const Description = styled.p`
  margin: 0;
  color: var(--app-muted-text, #5f6368);
  line-height: 1.55;
`;

const UploadArea = styled.div`
  border: 2px dashed ${({ $dragOver }) => $dragOver ? "var(--app-nav-active-bg, #202124)" : "var(--app-border, #bdbdbd)"};
  border-radius: 8px;
  background: ${({ $dragOver }) => $dragOver ? "var(--app-surface-soft, #f2f2f2)" : "var(--app-surface-hover, #fafafa)"};
`;

const UploadLabel = styled.label`
  min-height: 130px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 18px;
  text-align: center;
  cursor: pointer;

  svg { font-size: 24px; color: var(--app-icon-accent, #202124); }
  strong { max-width: 100%; overflow-wrap: anywhere; }
  small { color: var(--app-muted-text, #5f6368); }
`;

const FileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
`;

const SourceImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 220px;
  justify-self: center;
  object-fit: contain;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 6px;
`;

const SelectedImage = styled.div`
  display: grid;
  gap: 8px;
  min-width: 0;
`;

const RemoveImageButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-self: end;
  gap: 6px;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 7px;
  padding: 6px 10px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  font: inherit;
  cursor: pointer;
`;

const FieldLabel = styled.label`
  font-weight: 750;
`;

const Instructions = styled.textarea`
  width: 100%;
  min-height: 112px;
  box-sizing: border-box;
  resize: vertical;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 8px;
  padding: 10px 12px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  font: inherit;
  line-height: 1.5;

  &:focus-visible { outline: 2px solid #4285f4; outline-offset: 1px; }
`;

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  border: 0;
  border-radius: 8px;
  padding: 10px 14px;
  background: var(--app-nav-active-bg, #242424);
  color: var(--app-nav-active-text, #fff);
  font-weight: 750;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: progress; }
`;

const StatusMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 7px;
  padding: 10px 12px;
  background: ${({ $phase }) => $phase === "error" ? "var(--app-danger-bg, #fff3f1)" : "var(--app-surface-soft, #f4f6f8)"};
  color: ${({ $phase }) => $phase === "error" ? "var(--app-danger-text, #b3261e)" : "var(--app-surface-text, #202124)"};
  line-height: 1.5;
`;

const ResultHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 7px;
  padding: 5px 10px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  font: inherit;
  font-weight: 700;
  cursor: pointer;
`;

const ActionLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 34px;
  box-sizing: border-box;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 7px;
  padding: 5px 10px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  font-weight: 700;
  text-decoration: none;
`;

const ResultImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: 680px;
  justify-self: center;
  object-fit: contain;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 6px;
  background: #fff;
`;

const CodeArea = styled.textarea`
  width: 100%;
  min-height: 260px;
  box-sizing: border-box;
  resize: vertical;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);
  font: 13px/1.55 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  tab-size: 4;
`;

const CopyMessage = styled.small`
  color: var(--app-muted-text, #5f6368);
`;
